import { fetchScript } from "~/recipal-app/controllers/shared/requests";
import { onViewLoad } from "~/jose_modern/on_view_load.js";
import { Modal } from "~/jose_modern/modal";
import { once, onceEach } from "~/jose_modern/once";
import { Confirm } from "~/jose_modern/confirm";
import { Flash } from "~/jose_modern/flash";
import { safeSubmitForm } from "~/jose_modern/auto_submit.js";
import { Highlighter } from "~/jose_modern/highlighter";
import { setupTippyHint } from "~/jose_modern/hint_util";
import { debounce } from "lodash-es";
import tippy from "tippy.js";
import { updateAllProductionRunIngredientsPath } from "~/routes";
import { initializeVisibilityToggle } from "~/jose_modern/inventory/shared/toggle_visibility";
import { initializeShelfLifeLotCodeControls } from "./shelf_life_lot_code_controls";
import {
  LOT_CODE_MISSING_CLASS,
  isLotCodeRequired,
  markInputMissing,
  markRowMissingLotCodes,
  reportMissingLotCodes,
} from "./shared/lot_code_validation";

const CHANGED_CLASS = "changed-actual";

let actualsSaveInFlight = false;

function getActualId(element) {
  const form = element.closest("form");
  if (!form) return null;
  return form.dataset.actualId || null;
}

function initializeSaveButton() {
  const saveButton = document.getElementById("submit-production-run-ingredient-actuals");

  if (saveButton) {
    once(saveButton, "save-button-handler", (button) => {
      button.addEventListener("click", async function (e) {
        e.preventDefault();

        if (hasBlockingLotCodeIssues()) {
          updateActualsSaveValidity();
          highlightBlockingLotCodeIssues();
          return;
        }

        if (document.querySelectorAll(`.${CHANGED_CLASS}`).length === 0) {
          alert("No changes detected. Please make changes before saving.");
          return;
        }

        await saveChangedActuals();
      });
    });
  }
}

async function saveChangedActuals() {
  if (hasBlockingLotCodeIssues()) {
    updateActualsSaveValidity();
    highlightBlockingLotCodeIssues();
    return false;
  }

  const changedFields = document.querySelectorAll(`.${CHANGED_CLASS}`);
  if (changedFields.length === 0) return false;

  const formsToSubmit = new Map();
  changedFields.forEach((field) => {
    const form = field.closest("form");
    if (form) {
      const actualId = getActualId(field);
      if (actualId && !formsToSubmit.has(actualId)) {
        formsToSubmit.set(actualId, form);
      }
    }
  });

  if (isLotCodeRequired() && !validateActualsLotCodes(formsToSubmit)) return false;

  setActualsSaving(true);
  try {
    return await submitActualsForms(formsToSubmit);
  } catch (error) {
    alert("Error saving changes. Please try again.");
    return false;
  } finally {
    setActualsSaving(false);
  }
}

function setActualsSaving(saving) {
  actualsSaveInFlight = saving;

  const saveButton = document.getElementById("submit-production-run-ingredient-actuals");
  if (saveButton) {
    saveButton.classList.toggle("is-loading", saving);
    saveButton.classList.toggle("is-disabled", saving);
    saveButton.toggleAttribute("disabled", saving);
  }

  document.dispatchEvent(new CustomEvent("actuals:saving-changed", { detail: { saving } }));
  updateCloseButtonState();
}

function flagActualsError() {
  const actuals = document.querySelector(".actuals-table");
  if (!actuals) return;

  actuals.scrollIntoView({ behavior: "smooth", block: "start" });
  Highlighter.highlight(actuals);
}

function hasBlockingLotCodeIssues() {
  return Boolean(document.querySelector("production-run-lot-codes [data-lot-codes-invalid]"));
}

function highlightBlockingLotCodeIssues() {
  document.querySelectorAll("[data-lot-codes-editor][data-lot-codes-invalid]").forEach((editor) => {
    const errorInputs = editor.querySelectorAll("input.is-danger, input[data-errors]");

    if (errorInputs.length) {
      errorInputs.forEach((input) => Highlighter.highlight(input));
    } else {
      Highlighter.highlight(editor.closest(".actuals-table-row") ?? editor);
    }
  });
}

function setSaveBlockedHint(button, blocked) {
  if (blocked) {
    button._saveBlockedHint ||= tippy(button, {
      content: "Fix the highlighted lot code issues before saving",
      theme: "error",
      placement: "top",
    });
  } else {
    button._saveBlockedHint?.destroy();
    button._saveBlockedHint = null;
  }
}

function updateActualsSaveValidity() {
  const saveButton = document.getElementById("submit-production-run-ingredient-actuals");
  if (!saveButton) return;

  const blocked = hasBlockingLotCodeIssues();

  saveButton.classList.toggle("is-disabled", blocked);
  saveButton.setAttribute("aria-disabled", String(blocked));
  setSaveBlockedHint(saveButton, blocked);

  if (blocked) {
    saveButton.classList.remove("has-unsaved", "is-pulsing-repeat-slow");
  } else if (document.querySelector(`.${CHANGED_CLASS}`)) {
    saveButton.classList.add("has-unsaved", "is-pulsing-repeat-slow");
  }

  updateSaveButtonVisibility();
  updateUnsavedGuard();
}

function updateSaveButtonVisibility() {
  const saveButton = document.getElementById("submit-production-run-ingredient-actuals");
  if (!saveButton) return;

  const revealed =
    Boolean(document.querySelector(`.${CHANGED_CLASS}`)) || hasUnsavedActualsChanges();
  saveButton.classList.toggle("is-hidden", !revealed);
}

// --- Unsaved actuals guard -------------------------------------------------
// This page opts out of the global warn_unsaved_changes system so it can offer
// an action-aware modal (leaving vs. closing the run) that spells out what
// changed. The native beforeunload dialog stays as the only reliable guard for
// hard exits (tab close, reload) -- browsers forbid custom UI there.

const ACTUALS_INPUT_SELECTOR =
  ".production-run-ingredient-quantity, .production-run-ingredient-unit, .safety-actual-value";

const UNSAVED_ACTUALS_MESSAGE =
  "You have unsaved actual amounts or lot codes. If you leave, they will be lost.";

const UNSAVED_MODAL_COPY = {
  navigate: {
    title: "Unsaved actual amounts",
    message:
      "You're about to leave this production run, but you have unsaved changes to actual amounts or lot codes. If you leave, they will be lost.",
    save: "Save changed actuals/lots",
    discard: "Leave without saving",
    cancel: "Stay on this page",
  },
  close: {
    title: "Close with unsaved changes?",
    message:
      "You're about to close this production run, but you have unsaved changes to actual amounts or lot codes. Closing won't include them unless you save first.",
    save: "Save, then close",
    discard: "Close without saving",
    cancel: "Keep editing",
  },
};

// The global watcher used to seed these baselines; record them ourselves so the
// changed-actual detection in updateChangedStatus keeps working.
function recordActualsBaselines() {
  document.querySelectorAll(ACTUALS_INPUT_SELECTOR).forEach((input) => {
    if (input._warnUnsavedOriginalValue === undefined) {
      input._warnUnsavedOriginalValue = input.value;
    }
  });
}

function hasUnsavedActualsChanges() {
  if (
    document.querySelector(
      `.production-run-ingredient-quantity.${CHANGED_CLASS},` +
        `.production-run-ingredient-unit.${CHANGED_CLASS},` +
        `.safety-actual-value.${CHANGED_CLASS}`
    )
  ) {
    return true;
  }

  return Boolean(
    document.querySelector(
      "production-run-lot-codes [data-lot-codes-editor][data-lot-codes-changed]"
    )
  );
}

let unsavedGuardRegistered = false;

function beforeUnloadGuard(e) {
  e.preventDefault();
  e.returnValue = UNSAVED_ACTUALS_MESSAGE;
  return UNSAVED_ACTUALS_MESSAGE;
}

function updateUnsavedGuard() {
  const dirty = hasUnsavedActualsChanges();
  if (dirty && !unsavedGuardRegistered) {
    window.addEventListener("beforeunload", beforeUnloadGuard);
    unsavedGuardRegistered = true;
  } else if (!dirty && unsavedGuardRegistered) {
    clearUnsavedGuard();
  }
}

function clearUnsavedGuard() {
  window.removeEventListener("beforeunload", beforeUnloadGuard);
  unsavedGuardRegistered = false;
}

function collectActualsChangeSummary() {
  const summary = [];
  document.querySelectorAll(".actuals-table-row").forEach((row) => {
    const aspects = [];
    if (row.querySelector(`.production-run-ingredient-quantity.${CHANGED_CLASS}`)) {
      aspects.push("amount");
    }
    if (row.querySelector(`.production-run-ingredient-unit.${CHANGED_CLASS}`)) {
      aspects.push("unit");
    }
    if (row.querySelector(`.safety-actual-value.${CHANGED_CLASS}`)) {
      aspects.push("recorded value");
    }
    if (row.querySelector("[data-lot-codes-editor][data-lot-codes-changed]")) {
      aspects.push("lot codes");
    }
    if (aspects.length === 0) return;
    summary.push({ name: row.dataset.ingredientName || "Ingredient", aspects });
  });
  return summary;
}

function openUnsavedModal(mode) {
  const copy = UNSAVED_MODAL_COPY[mode];
  const modal = document.getElementById("actuals-unsaved-modal");
  if (!modal) {
    return Promise.resolve(window.confirm(copy.message) ? "discard" : "cancel");
  }

  modal.querySelector("[data-unsaved-title]").textContent = copy.title;
  modal.querySelector("[data-unsaved-message]").textContent = copy.message;
  modal.querySelector("[data-unsaved-discard]").textContent = copy.discard;
  modal.querySelector("[data-unsaved-cancel-label]").textContent = copy.cancel;

  const saveButton = modal.querySelector("[data-unsaved-save]");
  saveButton.textContent = copy.save;
  const canSave = !hasBlockingLotCodeIssues();
  saveButton.classList.toggle("is-hidden", !canSave);
  modal.querySelector("[data-unsaved-blocked]").classList.toggle("is-hidden", canSave);

  const list = modal.querySelector("[data-unsaved-changes-list]");
  list.replaceChildren();
  collectActualsChangeSummary().forEach(({ name, aspects }) => {
    const item = document.createElement("li");
    const label = document.createElement("strong");
    label.textContent = name;
    item.append(label, document.createTextNode(` -- ${aspects.join(", ")}`));
    list.append(item);
  });

  return new Promise((resolve) => {
    const discardButton = modal.querySelector("[data-unsaved-discard]");
    const cancelEls = modal.querySelectorAll("[data-unsaved-cancel]");
    let saving = false;

    const finish = (result) => {
      modal.classList.remove("is-active");
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("actuals:saving-changed", onSavingChanged);
      cancelEls.forEach((el) => el.removeEventListener("click", onCancel));
      saveButton.removeEventListener("click", onSave);
      discardButton.removeEventListener("click", onDiscard);
      resolve(result);
    };

    const setModalSaving = (value) => {
      saving = value;
      saveButton.classList.toggle("is-loading", value);
      saveButton.toggleAttribute("disabled", value);
    };

    const onSavingChanged = (e) => setModalSaving(e.detail.saving);

    const onSave = async () => {
      if (saving) return;
      const saved = await saveChangedActuals();
      if (saved) {
        finish("save");
      } else {
        finish("failed");
        flagActualsError();
      }
    };
    const onDiscard = () => finish("discard");
    const onCancel = () => finish("cancel");
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        finish("cancel");
      }
    };

    cancelEls.forEach((el) => el.addEventListener("click", onCancel));
    saveButton.addEventListener("click", onSave);
    discardButton.addEventListener("click", onDiscard);
    document.addEventListener("keydown", onKey);
    document.addEventListener("actuals:saving-changed", onSavingChanged);

    setModalSaving(actualsSaveInFlight);
    modal.classList.add("is-active");
  });
}

async function confirmUnsavedThen(mode, proceed) {
  const choice = await openUnsavedModal(mode);
  if (choice !== "save" && choice !== "discard") return;

  clearUnsavedGuard();
  proceed();
}

function isLeavingLink(link, ev) {
  if (!link) return false;
  if (link.target === "_blank") return false;
  if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return false;
  if (link.hasAttribute("data-remote") || link.hasAttribute("data-method")) return false;
  if (link.hasAttribute("data-no-warn-unsaved")) return false;
  if (link.closest("#actuals-unsaved-modal")) return false;

  const href = link.getAttribute("href");
  if (!href || href.startsWith("#") || href.startsWith("javascript:")) return false;

  let url;
  try {
    url = new URL(link.href, window.location.origin);
  } catch {
    return false;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return false;

  // A same-page hash change doesn't unload anything.
  if (
    url.pathname === window.location.pathname &&
    url.search === window.location.search &&
    url.hash
  ) {
    return false;
  }
  return true;
}

function initializeUnsavedNavGuard() {
  once(document, "actuals-unsaved-nav-guard", (doc) => {
    doc.addEventListener(
      "click",
      (ev) => {
        if (!hasUnsavedActualsChanges()) return;
        const link = ev.target.closest("a[href]");
        if (!isLeavingLink(link, ev)) return;

        ev.preventDefault();
        ev.stopImmediatePropagation();

        confirmUnsavedThen("navigate", () => {
          window.location.href = link.href;
        });
      },
      true
    );
  });
}

// Capture-phase submit guard, so it pre-empts the bubble-phase close-form
// validation and single-submit until the user resolves the unsaved changes.
function initializeUnsavedCloseGuard() {
  once(document, "actuals-unsaved-close-guard", (doc) => {
    doc.addEventListener(
      "submit",
      (ev) => {
        const form = ev.target;
        if (!form.action || !form.action.includes("/close")) return;
        if (form._unsavedGuardBypass) {
          form._unsavedGuardBypass = false;
          return;
        }
        if (!hasUnsavedActualsChanges()) return;

        ev.preventDefault();
        ev.stopImmediatePropagation();

        confirmUnsavedThen("close", () => {
          form._unsavedGuardBypass = true;
          form.requestSubmit();
        });
      },
      true
    );
  });
}

function initializeLotCodeEditorChanges() {
  once(document, "lot-code-editor-changes", (doc) => {
    const recheck = (e) => {
      if (
        !e.target.closest("production-run-lot-codes") &&
        !e.target.closest(".production-run-ingredient-quantity")
      ) {
        return;
      }
      setTimeout(updateActualsSaveValidity, 0);
    };

    doc.addEventListener("change", recheck);
    doc.addEventListener("input", recheck);
  });
}

function updateChangedStatus(element) {
  const original = element._warnUnsavedOriginalValue ?? "";
  if (valueDiffersFromBaseline(element.value, original)) {
    element.classList.add(CHANGED_CLASS);
  } else {
    element.classList.remove(CHANGED_CLASS);
  }
}

// Compares numbers or strings if we can't
function valueDiffersFromBaseline(current, original) {
  const currentNumber = Number(current);
  const originalNumber = Number(original);
  const bothNumeric =
    current.trim() !== "" &&
    original.trim() !== "" &&
    Number.isFinite(currentNumber) &&
    Number.isFinite(originalNumber);

  return bothNumeric ? currentNumber !== originalNumber : current !== original;
}

function validateActualsLotCodes(formsToSubmit) {
  const invalidElements = [];

  formsToSubmit.forEach((form) => {
    const row = form.closest(".actuals-table-row");
    if (!row) return;
    const invalid = markRowMissingLotCodes(row, { highlight: true });
    if (invalid) invalidElements.push(invalid);
  });

  return reportMissingLotCodes(
    invalidElements,
    "Lot codes are required for all adjustments per your account settings. Please fill in the highlighted fields."
  );
}

function validateCloseLotCodes() {
  const invalidElements = [];

  const fgLotCode = document.getElementById("production_run_lot_code");
  if (fgLotCode && !fgLotCode.value.trim()) {
    invalidElements.push(
      markInputMissing(fgLotCode, fgLotCode.closest(".field") || fgLotCode, { highlight: true })
    );
  }

  document.querySelectorAll(".actuals-table-row").forEach((row) => {
    const invalid = markRowMissingLotCodes(row, { highlight: true });
    if (invalid) invalidElements.push(invalid);
  });

  return reportMissingLotCodes(
    invalidElements,
    "Lot codes are required to close this production run per your account settings. Please fill in the highlighted fields."
  );
}

const CLOSE_REQUIRED_FIELDS = [
  { id: "production_run_quantity_actual", label: "Actual amount produced" },
  { id: "production_run_hours", label: "Hours" },
];

const CLOSE_DISABLED_HINT =
  "Enter the actual amount produced and the hours before closing this production run.";

function closeRequiredInputs() {
  return CLOSE_REQUIRED_FIELDS.map(({ id, label }) => ({
    input: document.getElementById(id),
    label,
  })).filter(({ input }) => input);
}

function hasMissingCloseFields() {
  return closeRequiredInputs().some(({ input }) => input.value.trim() === "");
}

// The server rejects a close without an actual amount and hours; flag them inline
// (via data-errors tooltips) so the user fixes them without a failed round trip.
function validateCloseRequiredFields() {
  const invalidInputs = [];
  closeRequiredInputs().forEach(({ input, label }) => {
    if (input.value.trim() === "") {
      input.setAttribute("data-errors", JSON.stringify({ [label]: ["is required to close"] }));
      Highlighter.highlight(input.closest(".field") || input);
      invalidInputs.push(input);
    } else {
      input.removeAttribute("data-errors");
    }
  });
  return invalidInputs;
}

function updateCloseButtonState() {
  const closeButton = document.getElementById("close-production-run-button");
  if (!closeButton) return;

  const missingFields = hasMissingCloseFields();

  closeButton.classList.toggle("is-disabled", actualsSaveInFlight || missingFields);
  // Missing-fields stays clickable so a click surfaces the field errors; only an
  // in-flight save hard-blocks the submit.
  closeButton.toggleAttribute("disabled", actualsSaveInFlight);

  if (missingFields) {
    closeButton.dataset.hint = CLOSE_DISABLED_HINT;
    closeButton.dataset.hintAllowClickLink = "true";
    if (!closeButton._tippy) setupTippyHint(closeButton);
  } else {
    closeButton.removeAttribute("data-hint");
    closeButton._tippy?.destroy();
  }
}

function resetSingleSubmit(form) {
  form.removeAttribute("is-submitting");
  form.querySelectorAll("button, input[type=submit]").forEach((btn) => {
    btn.classList.remove("is-loading");
    btn.removeAttribute("disabled");
  });
}

function initializeCloseFormValidation() {
  once(document, "close-form-validation", (doc) => {
    doc.addEventListener("submit", (e) => {
      const form = e.target;
      if (!form.action || !form.action.includes("/close")) return;

      const missingRequired = validateCloseRequiredFields();
      if (missingRequired.length > 0) {
        e.preventDefault();
        resetSingleSubmit(form);
        Flash.error(
          "Enter the Actual Amount Produced and the Hours before closing this production run."
        );
        missingRequired[0].scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }

      if (isLotCodeRequired() && !validateCloseLotCodes()) {
        e.preventDefault();
        resetSingleSubmit(form);
      }
    });

    const clearOnFill = (e) => {
      if (!e.target.matches("#production_run_quantity_actual, #production_run_hours")) return;
      if (e.target.value.trim() !== "") e.target.removeAttribute("data-errors");
      updateCloseButtonState();
    };
    doc.addEventListener("input", clearOnFill);
    doc.addEventListener("change", clearOnFill);
  });
}

function initUnsavedChangeVisualIndication() {
  once(document, "production-runs-indicate-changes", (doc) => {
    const handleFieldChange = (e) => {
      const field = e.target;
      updateChangedStatus(field);

      if (field.classList.contains(CHANGED_CLASS)) {
        const saveBtn = document.getElementById("submit-production-run-ingredient-actuals");
        saveBtn?.classList.add("has-unsaved", "is-pulsing-repeat-slow");
      }

      updateSaveButtonVisibility();
      updateUnsavedGuard();
    };

    doc.addEventListener("input", (e) => {
      if (e.target.matches(".production-run-ingredient-quantity, .safety-actual-value")) {
        handleFieldChange(e);
      }
    });

    doc.addEventListener("change", (e) => {
      if (e.target.matches(".production-run-ingredient-unit")) {
        handleFieldChange(e);
      }
    });
  });
}

function initializeRecipeSelectionConfirmation() {
  once(document, "recipe-selection-confirmation", (doc) => {
    doc.addEventListener(
      "click",
      async (ev) => {
        const selectLink = ev.target.closest(".js-select-for-production");
        if (!selectLink) return;

        ev.preventDefault();
        ev.stopPropagation();

        const container = document.getElementById("js-pr-search-results");
        const currentRecipeName = container?.dataset.currentRecipeName?.trim();

        if (!currentRecipeName) {
          await submitRecipeSelection(selectLink);
          return;
        }

        const newRecipeName = selectLink.dataset.recipeName;
        const confirmMessage = `<p>Switch this production run from <strong>${currentRecipeName}</strong> to <strong>${newRecipeName}</strong>?</p>`;

        const result = await Confirm.confirm(confirmMessage, {
          confirm: "Switch Recipe",
          cancel: "Cancel",
        });

        if (result && result.response) {
          await submitRecipeSelection(selectLink);
        }
      },
      true // use capture phase to intercept before Rails UJS
    );
  });
}

async function submitRecipeSelection(link) {
  try {
    await fetchScript(link.href, { method: "PUT" });
  } catch (error) {
    Flash.error("Error switching recipe. Please refresh the page and try again.");
    console.error("Error updating production run recipe:", error);
  }
}

async function submitActualsForms(formsToSubmit) {
  const production_run_ingredients = {};

  Array.from(formsToSubmit.values()).forEach((form, index) => {
    const actualId = form.dataset.actualId;
    const quantityInput = form.querySelector(".production-run-ingredient-quantity");
    const unitSelect = form.querySelector(".production-run-ingredient-unit");
    const lotCodesJson = form.querySelector("[data-lot-codes-json]");
    const lot_codes_attributes = lotCodesJson ? JSON.parse(lotCodesJson.value || "[]") : [];

    const safetyActualValue = form.querySelector(".safety-actual-value");

    production_run_ingredients[index] = {
      id: actualId,
      production_run_ingredient: {
        quantity: safetyActualValue?.value ?? quantityInput?.value,
        unit: unitSelect?.value,
        lot_codes_attributes,
      },
    };
  });

  let resolveResult;
  const result = new Promise((resolve) => (resolveResult = resolve));
  const onResult = (e) => resolveResult(Boolean(e.detail?.ok));
  document.addEventListener("actuals:save-result", onResult, { once: true });

  try {
    await fetchScript(updateAllProductionRunIngredientsPath(), {
      method: "POST",
      body: JSON.stringify({ production_run_ingredients }),
    });
  } catch (error) {
    document.removeEventListener("actuals:save-result", onResult);
    throw error;
  }

  return result;
}

function initializeEnterKeySubmission() {
  once(document, "actuals-enter-submit", (doc) => {
    doc.addEventListener("keydown", (ev) => {
      if (
        ev.key === "Enter" &&
        ev.target.matches(
          ".production-run-ingredient-quantity, .production-run-ingredient-unit, .safety-actual-value"
        )
      ) {
        ev.preventDefault();
        const saveButton = document.getElementById("submit-production-run-ingredient-actuals");
        if (saveButton) {
          saveButton.click();
        }
      }
    });
  });
}

function initializeLotCodeFormulaModal() {
  once(document, "lot-code-formula-modal", (doc) => {
    doc.addEventListener("click", (ev) => {
      if (ev.target.closest("#lot-code-formula-modal-load")) {
        ev.preventDefault();
        Modal.show("#lot-code-formula-modal");
      }
    });
  });
}

function initializeAutofillConfirmation() {
  once(document, "autofill-confirmation", (doc) => {
    doc.addEventListener(
      "click",
      async (ev) => {
        const autofillButton = ev.target.closest("#autofill_lot_codes_button");
        if (!autofillButton) return;

        // Check if there are any populated lot codes in the form
        const lotCodeFields = document.querySelectorAll(
          "[data-lot-codes-editor] [data-lot-code-input]"
        );
        const hasPopulatedLotCodes = Array.from(lotCodeFields).some(
          (field) => (field.dataset.lotCode ?? "").trim() !== ""
        );

        if (!hasPopulatedLotCodes) {
          // No lot codes populated, proceed normally
          return;
        }

        ev.preventDefault();
        ev.stopPropagation();

        const confirmMessage = `<p>There are already some lot codes populated. <strong>Autofill Lots will clear all existing lot codes and replace them.</strong></p><p>Continue?</p>`;

        const result = await Confirm.confirm(confirmMessage, {
          confirm: "Continue",
          cancel: "Cancel",
        });

        if (result && result.response) {
          // User confirmed, trigger the autofill by navigating to the link
          await fetchScript(autofillButton.href, { method: "GET" });
        }
      },
      true // use capture phase to intercept before Rails UJS
    );
  });
}

function initializeFilterToggle() {
  once(document, "pr-filter-toggle", (doc) => {
    doc.addEventListener("click", (e) => {
      const button = e.target.closest("[data-pr-filter-toggle]");
      if (!button) return;

      const target = document.getElementById(button.getAttribute("aria-controls"));
      if (!target) return;

      const isExpanded = button.getAttribute("aria-expanded") === "true";
      const nowExpanded = !isExpanded;

      button.setAttribute("aria-expanded", String(nowExpanded));
      target.classList.toggle("is-hidden", !nowExpanded);

      const filtersOpenInput = document.getElementById("pr-filters-open");
      if (filtersOpenInput) filtersOpenInput.value = nowExpanded ? "1" : "";

      const summary = document.querySelector("[data-pr-filter-summary]");
      const hasFilters = summary?.dataset.hasFilters === "1";
      if (summary) summary.classList.toggle("is-hidden", nowExpanded || !hasFilters);
      button.classList.toggle("is-info", nowExpanded || hasFilters);
    });
  });
}

function initializeFilterSearch() {
  onceEach(document.querySelectorAll("[data-pr-filter-search]"), "pr-filter-search", (input) => {
    const debouncedSubmit = debounce(() => safeSubmitForm(input.form), 300);
    input.addEventListener("input", debouncedSubmit);
  });
}

let pendingFilterUrl = null;

function initializeFilterUrlSync() {
  once(document, "pr-filter-url-sync", (doc) => {
    doc.addEventListener("submit", (e) => {
      const form = e.target.closest("#pr-filter-form");
      if (!form) return;
      const url = new URL(form.action);
      for (const [key, value] of new FormData(form).entries()) {
        if (key === "filters_open") continue;
        if (value) url.searchParams.set(key, value);
      }
      pendingFilterUrl = url.pathname + url.search;
    });

    doc.addEventListener("click", (e) => {
      const link = e.target.closest("a[data-remote]");
      if (!link) return;
      if (!link.closest(".production-run-filters, .open-list, .closed-list")) return;
      pendingFilterUrl = link.getAttribute("href");
    });

    doc.addEventListener("partial:replace", (e) => {
      if (!pendingFilterUrl) return;
      if (e.partial !== "production_runs/filters") return;
      history.replaceState(null, "", pendingFilterUrl);
      pendingFilterUrl = null;
    });
  });
}

const scan = () => {
  initializeFilterToggle();
  initializeFilterSearch();
  initializeFilterUrlSync();
  initializeVisibilityToggle();
  initializeLotCodeEditorChanges();
  recordActualsBaselines();
  initializeSaveButton();
  initializeUnsavedNavGuard();
  initializeUnsavedCloseGuard();
  updateActualsSaveValidity();
  initUnsavedChangeVisualIndication();
  initializeLotCodeFormulaModal();
  initializeShelfLifeLotCodeControls();
  initializeRecipeSelectionConfirmation();
  initializeAutofillConfirmation();
  initializeEnterKeySubmission();
  initializeCloseFormValidation();
  updateCloseButtonState();

  // Clear lot-code-required-missing class when the user fills in a lot code.
  once(document, "lot-code-missing-clear", (doc) => {
    doc.addEventListener("input", (e) => {
      const field = e.target.closest("[data-lot-code-input]");
      if (field && e.target.value.trim()) {
        field.classList.remove(LOT_CODE_MISSING_CLASS);
      } else if (e.target.matches("#production_run_lot_code") && e.target.value.trim()) {
        e.target.classList.remove(LOT_CODE_MISSING_CLASS);
      }
    });
  });

  if (isLotCodeRequired()) {
    highlightSavedRowsMissingLotCodes();
  }
};

function highlightSavedRowsMissingLotCodes() {
  document.querySelectorAll(".actuals-table-row").forEach((row) => {
    const quantityInput = row.querySelector(".production-run-ingredient-quantity");
    if (!quantityInput) return;

    const quantity = parseFloat(quantityInput.value);
    if (!quantity || quantity === 0) return;

    markRowMissingLotCodes(row);
  });
}

onViewLoad(/^production_runs#/, () => {
  scan();
  document.addEventListener("partial:replace", () => {
    requestAnimationFrame(() => {
      scan();
    });
  });
});
